import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import Pagination from "@/components/ui/PaginationWrapper";
import { Download, Trash2, Plus, KeyRound, X, CheckCircle, AlertCircle, Loader2, Smartphone } from 'lucide-react';
import { userService } from '../_services';

function ManageUser({
    text,
    wholesaler_name,
    closeResetPasswordPopup,
    openDeletePopup,
    confirmSection,
    manageUserEmail,
    manageUserName,
    manageUserId,
    manageUserPassword,
    manageUserPagin,
    manageUserSearch
}) {
    const [loading, setLoading] = useState(true);
    const [containerHide, setContainerHide] = useState(false);
    const [userList, setUserList] = useState([]);
    const [userListTemp, setUserListTemp] = useState([]);
    const [mainAPIResult, setMainAPIResult] = useState([]);
    const [activePage, setActivePage] = useState(1);
    const [currentPaging, setCurrentPaging] = useState(1);
    const [searchValue, setSearchValue] = useState('');
    const [sortBy, setSortBy] = useState('all');
    const [dataNotFound, setDataNotFound] = useState('');
    const [opStatus, setOpStatus] = useState(false);
    const [notification, setNotification] = useState({ show: false, type: '', heading: '', message: '' });
    const [isMobile, setIsMobile] = useState(false);

    // Detect mobile screen
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };
        
        checkMobile();
        window.addEventListener('resize', checkMobile);
        
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const userdata = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('user'));
        } catch {
            return null;
        }
    }, []);

    const handleResponse = useCallback(async (response) => {
        try {
            const text = await response.text();
            return text ? JSON.parse(text) : [];
        } catch {
            return [];
        }
    }, []);

    const showNotification = useCallback((type, heading, message) => {
        setOpStatus(type === 'success');
        setNotification({ show: true, type, heading, message });
        setTimeout(() => {
            setNotification({ show: false, type: '', heading: '', message: '' });
        }, 5000);
    }, []);

    const getDistributorList = useCallback(async () => {
        if (!userdata) return;
        
        setLoading(true);

        try {
            const response = await fetch('/api/ws/Distributor_DashList', {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json', 
                    'X-Browser-Url': window.location.href 
                },
                body: JSON.stringify({
                    roleName: userdata.paramsname,
                    wholesaler_Id: userdata.wholesaler_Id,
                    roleType: userdata.userrole
                })
            });

            const apidata = await handleResponse(response);
            
            if (apidata?.length > 0) {
                // Add serial numbers
                const dataWithSrNo = apidata.map((item, index) => ({
                    ...item,
                    srNo: index + 1
                }));
                
                const usertemplistobj = dataWithSrNo.length > 10 ? dataWithSrNo.slice(0, 10) : dataWithSrNo;
                
                setUserList(dataWithSrNo);
                setMainAPIResult(dataWithSrNo);
                setUserListTemp(usertemplistobj);
                setSortBy('all');
                setContainerHide(true);
                
                // Handle pagination from props
                if (manageUserPagin > 1) {
                    const tempList = dataWithSrNo.length > 10 
                        ? dataWithSrNo.slice((manageUserPagin - 1) * 10, manageUserPagin * 10) 
                        : dataWithSrNo;
                    setActivePage(manageUserPagin);
                    setUserListTemp(tempList);
                    setCurrentPaging(manageUserPagin);
                }
                
                // Handle initial search from props
                if (manageUserSearch && !userService.Check_Null_Empty(manageUserSearch)) {
                    setSearchValue(manageUserSearch);
                    const filtered = filterRecordsByText(dataWithSrNo, manageUserSearch);
                    const filteredTemp = filtered.length > 10 ? filtered.slice(0, 10) : filtered;
                    setUserList(filtered);
                    setUserListTemp(filteredTemp);
                }
            } else {
                setUserList([]);
                setUserListTemp([]);
                setDataNotFound('No users found');
                setContainerHide(true);  // Show the container so empty state message can display
            }
        } catch (err) {
            setDataNotFound('Unable to load users. Please try again.');
            setContainerHide(true);  // Show the container so error message can display
        } finally {
            setLoading(false);
        }
    }, [userdata, manageUserPagin, manageUserSearch, handleResponse]);

    const filterRecordsByText = (data, searchText) => {
        const lowerSearch = searchText.toLowerCase();
        return data.filter(item => 
            item.userName?.toLowerCase().includes(lowerSearch) || 
            item.userEmail?.toLowerCase().includes(lowerSearch)
        ).map((item, index) => ({ ...item, srNo: index + 1 }));
    };

    // Handle confirmation actions from props
    const handleDeleteUser = useCallback(async () => {
        setLoading(true);
        
        try {
            const response = await fetch('/api/user/RemoveDistributorAccount', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    distName: manageUserName,
                    distEmail: manageUserEmail,
                    wholeSaler: userdata?.paramsname
                })
            });

            const apidata = await handleResponse(response);
            
            if (apidata?.length > 0 && apidata[0].status_code === "1") {
                showNotification('success', 'Success', apidata[0].status_message);
                getDistributorList();
            } else {
                showNotification('error', 'Error', apidata?.[0]?.status_message || 'Whoops, something went wrong. Please try again');
            }
        } catch (err) {
            showNotification('error', 'Error', 'Whoops, something went wrong. Please try again');
        } finally {
            setLoading(false);
        }
    }, [manageUserName, manageUserEmail, userdata, handleResponse, showNotification, getDistributorList]);

    const handleCreateDashboard = useCallback(async () => {
        setLoading(true);
        
        try {
            const response = await fetch('/api/user/CreateDistributorAccount', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    distName: manageUserName,
                    distEmail: manageUserEmail,
                    wholeSaler: userdata?.paramsname,
                    wholesaler_Id: userdata?.wholesaler_Id
                })
            });

            const apidata = await handleResponse(response);
            
            if (apidata?.length > 0 && apidata[0].status_code === "1") {
                showNotification('success', 'Success', apidata[0].status_message);
                getDistributorList();
            } else {
                showNotification('error', 'Error', apidata?.[0]?.status_message || 'Whoops, something went wrong. Please try again');
            }
        } catch (err) {
            showNotification('error', 'Error', 'Whoops, something went wrong. Please try again');
        } finally {
            setLoading(false);
        }
    }, [manageUserName, manageUserEmail, userdata, handleResponse, showNotification, getDistributorList]);

    const handleResetPassword = useCallback(async () => {
        if (!manageUserPassword || manageUserPassword.length < 2) return;
        
        setLoading(true);
        
        try {
            const response = await fetch('/api/user/ResetDistributorAccountPwd', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    distEmail: manageUserEmail,
                    distName: manageUserName,
                    Wholesaler: userdata?.paramsname,
                    NewPassword: manageUserPassword[0],
                    ConfirmNewPassword: manageUserPassword[1]
                })
            });

            const apidata = await handleResponse(response);
            
            if (apidata?.length > 0 && apidata[0].status_code === "1") {
                showNotification('success', 'Success', apidata[0].status_message);
                getDistributorList();
            } else {
                showNotification('error', 'Error', apidata?.[0]?.status_message || 'Whoops, something went wrong. Please try again');
            }
        } catch (err) {
            showNotification('error', 'Error', 'Whoops, something went wrong. Please try again');
        } finally {
            setLoading(false);
        }
    }, [manageUserEmail, manageUserName, manageUserPassword, userdata, handleResponse, showNotification, getDistributorList]);

    useEffect(() => {
        getDistributorList();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (confirmSection === 'DeleteSection') {
            setContainerHide(true);
            handleDeleteUser();
        } else if (confirmSection === 'DashboardSection') {
            setContainerHide(true);
            handleCreateDashboard();
        } else if (confirmSection === 'ResendPasswordSection') {
            setContainerHide(true);
            handleResetPassword();
        }
    }, [confirmSection]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSearchChange = useCallback((e) => {
        const searchText = e.target.value;
        setSearchValue(searchText);
        setSortBy('all');
        
        if (searchText.length > 0) {
            const filtered = filterRecordsByText(mainAPIResult, searchText);
            
            if (filtered.length === 0) {
                setDataNotFound('No records found');
            } else {
                setDataNotFound('');
            }
            
            const filteredTemp = filtered.length > 10 ? filtered.slice(0, 10) : filtered;
            setActivePage(1);
            setUserList(filtered);
            setUserListTemp(filteredTemp);
        } else {
            const dataWithSrNo = mainAPIResult.map((item, index) => ({ ...item, srNo: index + 1 }));
            const usertemplistobj = dataWithSrNo.length > 10 ? dataWithSrNo.slice(0, 10) : dataWithSrNo;
            setActivePage(1);
            setUserList(dataWithSrNo);
            setUserListTemp(usertemplistobj);
            setDataNotFound('');
        }
    }, [mainAPIResult]);

    const handleTabChange = useCallback((tab) => {
        setSearchValue('');
        setSortBy(tab);
        
        let filtered;
        if (tab === 'all') {
            filtered = mainAPIResult;
        } else if (tab === 'withdashboard') {
            filtered = mainAPIResult.filter(item => item.userId !== "0");
        } else {
            filtered = mainAPIResult.filter(item => item.userId === "0");
        }
        
        const dataWithSrNo = filtered.map((item, index) => ({ ...item, srNo: index + 1 }));
        const usertemplistobj = dataWithSrNo.length > 10 ? dataWithSrNo.slice(0, 10) : dataWithSrNo;
        
        setActivePage(1);
        setUserList(dataWithSrNo);
        setUserListTemp(usertemplistobj);
    }, [mainAPIResult]);

    const handlePageChange = useCallback((pageNumber) => {
        let filtered;
        if (sortBy === 'withdashboard') {
            filtered = mainAPIResult.filter(item => item.userId !== "0");
        } else if (sortBy === 'withoutdashboard') {
            filtered = mainAPIResult.filter(item => item.userId === "0");
        } else {
            filtered = mainAPIResult;
        }
        
        const dataWithSrNo = filtered.map((item, index) => ({ ...item, srNo: index + 1 }));
        const usertemplistobj = dataWithSrNo.length > 10 
            ? dataWithSrNo.slice((pageNumber - 1) * 10, pageNumber * 10) 
            : dataWithSrNo;
        
        setActivePage(pageNumber);
        setUserListTemp(usertemplistobj);
        setCurrentPaging(pageNumber);
    }, [mainAPIResult, sortBy]);

    const isJdUser = userdata?.wholesaler_Id === 61;
    const [inlineError, setInlineError] = useState('');

    const handleSpecialCase = useCallback(() => {
        setInlineError('This action is not available');
        setTimeout(() => setInlineError(''), 3000);
    }, []);

    const handleDeleteClick = useCallback((userName, userEmail, userId) => {
        if (isJdUser) { handleSpecialCase(); return; }
        closeResetPasswordPopup?.(userName, userEmail, userId, 'Are you sure want to delete this dashboard?', 'DeleteSection', currentPaging, searchValue);
        openDeletePopup?.();
    }, [isJdUser, handleSpecialCase, closeResetPasswordPopup, openDeletePopup, currentPaging, searchValue]);

    const handleCreateDashboardClick = useCallback((userName, userEmail, userId) => {
        if (isJdUser) { handleSpecialCase(); return; }
        closeResetPasswordPopup?.(userName, userEmail, userId, 'Click on confirm to create a dashboard', 'DashboardSection', currentPaging, searchValue);
        openDeletePopup?.();
    }, [isJdUser, handleSpecialCase, closeResetPasswordPopup, openDeletePopup, currentPaging, searchValue]);

    const handleChangePasswordClick = useCallback((userName, userEmail, userId) => {
        if (isJdUser) { handleSpecialCase(); return; }
        closeResetPasswordPopup?.(userName, userEmail, userId, 'Change Password', 'ResendPasswordSection', currentPaging, searchValue);
        openDeletePopup?.();
    }, [isJdUser, handleSpecialCase, closeResetPasswordPopup, openDeletePopup, currentPaging, searchValue]);

    const handleExport = useCallback(() => {
        try {
            const tableData = [];
            tableData.push('Full Name,Email,Status');
            
            userList.forEach(item => {
                const status = item.userId === "0" ? 'No dashboard account' : 'Dashboard account exists';
                tableData.push(`${item.userName?.replace(/,/g, '')},${item.userEmail?.replace(/,/g, '')},${status}`);
            });
            
            const csvContent = tableData.join('\n');
            const blob = new Blob([csvContent], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'ListOfUser.csv';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (err) {
        }
    }, [userList]);

    const handleOpenChange = useCallback((open) => {
        if (!open) {
            closeResetPasswordPopup?.();
        }
    }, [closeResetPasswordPopup]);

    const paging = useMemo(() => {
        return userList.length % 10 !== 0 
            ? userList.length + 10 - (userList.length % 10) 
            : userList.length;
    }, [userList.length]);

    return (
        <Dialog open={true} onOpenChange={handleOpenChange}>
            <DialogContent className="sm:max-w-4xl h-[calc(100vh-3rem)]">
                <div className="flex flex-col h-full">
                    <DialogHeader className="flex-shrink-0">
                        <DialogTitle>{text}</DialogTitle>
                    </DialogHeader>

                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="h-8 w-8 animate-spin text-primary" />
                        </div>
                    ) : isMobile ? (
                        <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                            <Smartphone className="h-16 w-16 text-muted-foreground mb-4" />
                            <h3 className="text-lg font-semibold text-foreground mb-2">
                                Content Not Available
                            </h3>
                            <p className="text-sm text-muted-foreground">
                                Content not available for mobile screen. Please use a desktop or tablet device.
                            </p>
                        </div>
                    ) : (
                    <>
                        {/* Fixed Search, Tabs, and Export */}
                        <div className="flex-shrink-0 flex flex-wrap items-center gap-4">
                            {/* Search - Full width on small devices, auto width on larger devices */}
                            <Input
                                type="text"
                                placeholder="Search for a User..."
                                value={searchValue}
                                onChange={handleSearchChange}
                                disabled={loading}
                                className="w-full sm:w-auto sm:flex-1"
                            />

                            {containerHide && userList.length > 0 && (
                                <>
                                    {/* Tabs */}
                                    <Tabs value={sortBy} onValueChange={handleTabChange}>
                                        <TabsList className="h-10">
                                            <TabsTrigger value="all" className="h-9 px-4">All</TabsTrigger>
                                            <TabsTrigger value="withdashboard" className="h-9 px-4">With Dashboard</TabsTrigger>
                                            <TabsTrigger value="withoutdashboard" className="h-9 px-4">Without Dashboard</TabsTrigger>
                                        </TabsList>
                                    </Tabs>

                                    {/* Export Button */}
                                    <Button
                                        variant="secondary"
                                        size="icon"
                                        onClick={handleExport}
                                        title="Download report"
                                        className="h-10 w-auto px-3 gap-2"
                                    >
                                        <Download className="h-4 w-4" />
                                        Export
                                    </Button>
                                </>
                            )}
                        </div>

                        {containerHide && (
                            <>
                                {/* Scrollable Table */}
                                {userListTemp.length > 0 ? (
                                    <>
                                    <div className="flex-1 overflow-y-auto min-h-0 mt-4">
                                        <div className="border rounded-lg overflow-hidden">
                                            <Table>
                                                <TableHeader className="sticky top-0 bg-card z-10">
                                                    <TableRow>
                                                        <TableHead className="w-20">Sr. No.</TableHead>
                                                        <TableHead>Distributor Name</TableHead>
                                                        <TableHead>Email</TableHead>
                                                        <TableHead className="w-40">Action</TableHead>
                                                    </TableRow>
                                                </TableHeader>
                                        <TableBody>
                                            {userListTemp.map((row, index) => (
                                                <TableRow key={`${row.userId}-${index}`}>
                                                    <TableCell>{row.srNo}</TableCell>
                                                    <TableCell>{row.userName}</TableCell>
                                                    <TableCell>{row.userEmail}</TableCell>
                                                    <TableCell>
                                                        {row.userId === "0" ? (
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                className="h-8 w-8 relative group"
                                                                onClick={() => handleCreateDashboardClick(row.userName, row.userEmail, row.userId)}
                                                            >
                                                                <Plus className="h-4 w-4" />
                                                                <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 bg-primary text-primary-foreground">
                                                                    Create Dashboard
                                                                </span>
                                                            </Button>
                                                        ) : (
                                                            <div className="flex items-center gap-1">
                                                                <Button
                                                                    variant="ghost"
                                                                    size="icon"
                                                                    className="h-8 w-8 relative group"
                                                                    onClick={() => handleChangePasswordClick(row.userName, row.userEmail, row.userId)}
                                                                >
                                                                    <KeyRound className="h-4 w-4" />
                                                                    <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 bg-primary text-primary-foreground">
                                                                        Change Password
                                                                    </span>
                                                                </Button>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="icon"
                                                                    className="h-8 w-8 relative group text-destructive hover:text-destructive"
                                                                    onClick={() => handleDeleteClick(row.userName, row.userEmail, row.userId)}
                                                                >
                                                                    <Trash2 className="h-4 w-4" />
                                                                    <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 bg-primary text-primary-foreground">
                                                                        Delete Dashboard
                                                                    </span>
                                                                </Button>
                                                            </div>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>
                            </div>

                            {/* Fixed Pagination */}
                            {userList.length > 10 && (
                                <div className="flex-shrink-0 flex justify-center mt-4">
                                    <Pagination
                                        activePage={activePage}
                                        itemsCountPerPage={10}
                                        totalItemsCount={paging}
                                        pageRangeDisplayed={5}
                                        onChange={handlePageChange}
                                    />
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="text-center py-8 text-muted-foreground">
                            {dataNotFound || 'No records found'}
                        </div>
                    )}
                </>
            )}

            {/* Inline error message — matches ManageGroupNumber style */}
            {inlineError && (
                <div className="text-center mt-2 px-4 sm:px-6">
                    <p className="text-sm text-destructive">{inlineError}</p>
                </div>
            )}
        </>
    )}
                </div>

                {/* Notification Toast */}
                {notification.show && (
                    <div className={`fixed bottom-4 right-4 p-4 rounded-lg shadow-lg flex items-start gap-3 max-w-sm z-50 ${
                        notification.type === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
                    }`}>
                        {notification.type === 'success' ? (
                            <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                        ) : (
                            <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
                        )}
                        <div className="flex-1">
                            <h4 className={`font-medium ${notification.type === 'success' ? 'text-green-800' : 'text-red-800'}`}>
                                {notification.heading}
                            </h4>
                            <p className={`text-sm ${notification.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                                {notification.message}
                            </p>
                        </div>
                        <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-6 w-6 flex-shrink-0"
                            onClick={() => setNotification({ show: false, type: '', heading: '', message: '' })}
                        >
                            <X className="h-4 w-4" />
                        </Button>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

export default ManageUser;
